/**
 * tsukie-marketing-loader.js
 * Tsukie — Centralized Delayed Marketing Script Loader
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ STATUS: DORMANT BY DESIGN — do NOT "fix" it thinking it's broken.         │
 * │ The ID variables below (window.TSUKIE_GTM_ID / TSUKIE_FBPIXEL_ID /        │
 * │ TSUKIE_CLARITY_ID) are never set, and there are no gtm_id/facebook_pixel_ │
 * │ id/clarity_id fields in settings_schema.json, so nothing loads.           │
 * │ This is intentional: Microsoft Clarity (and other analytics) currently    │
 * │ load via Shopify APP EMBEDS (see config/settings_data.json). Wiring this  │
 * │ loader without first removing those app embeds would DOUBLE-load          │
 * │ analytics. To activate: (1) remove the duplicate app embed(s), (2) add    │
 * │ the settings fields, (3) render a Liquid wrapper that sets the window IDs.│
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Strategy:
 *   • Scripts load on FIRST USER INTERACTION (scroll, click, touchstart, keydown)
 *   • OR after a 5-second fallback timeout from window.load
 *   • Each script loads only ONCE (idempotent via loaded-flag registry)
 *   • GTM loads first; all other scripts are registered as dependents
 *   • Checkout (Razorpay Magic) is loaded separately in snippets/magicSidecartScript.liquid
 *
 * HOW TO ADD A SCRIPT:
 *   window.TsukieMarketing.register({ id: 'my-script', load: function() { ... } });
 *   — Call register() any time before or after the loader initialises.
 *     If the user has already interacted, the callback fires immediately.
 *
 * MODIFICATION NOTES:
 *   Replace placeholder IDs below with real ones:
 *     GTM_ID       → your GTM container ID  (e.g. GTM-XXXXXX)
 *     GA4_ID       → your GA4 measurement ID (e.g. G-XXXXXXXXXX)
 *     FBPIXEL_ID   → your Facebook Pixel ID  (e.g. 1234567890123)
 *     CLARITY_ID   → your Microsoft Clarity project ID
 *   Alternatively, leave the IDs as-is and pass them via Shopify theme settings
 *   by rendering this file through a Liquid wrapper snippet that interpolates
 *   {{ settings.gtm_id }} etc.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────────────
     0. Bail out in Shopify theme editor to avoid polluting preview analytics
  ───────────────────────────────────────────────────────────────────────── */
  if (
    window.Shopify &&
    (window.Shopify.designMode || document.documentElement.classList.contains('shopify-design-mode'))
  ) {
    return;
  }

  /* ─────────────────────────────────────────────────────────────────────────
     1. State
  ───────────────────────────────────────────────────────────────────────── */
  var _triggered = false;
  var _queue = [];      // scripts registered before trigger
  var _registry = {};   // id → true once loaded

  /* ─────────────────────────────────────────────────────────────────────────
     2. Public API  (available immediately as window.TsukieMarketing)
  ───────────────────────────────────────────────────────────────────────── */
  window.TsukieMarketing = {
    /**
     * Register a marketing script to be loaded on first interaction.
     * @param {Object} spec
     * @param {string}   spec.id    - Unique identifier; prevents double-loading.
     * @param {Function} spec.load  - Callback that performs the actual loading.
     */
    register: function (spec) {
      if (!spec || !spec.id || typeof spec.load !== 'function') return;
      if (_registry[spec.id]) return; // already loaded
      if (_triggered) {
        // Loader already fired — run immediately
        _runSpec(spec);
      } else {
        _queue.push(spec);
      }
    },

    /** Returns true if scripts have been triggered. */
    isTriggered: function () {
      return _triggered;
    }
  };

  /* ─────────────────────────────────────────────────────────────────────────
     3. Core trigger — runs once
  ───────────────────────────────────────────────────────────────────────── */
  function _trigger() {
    if (_triggered) return;
    _triggered = true;

    // Remove all interaction listeners
    _EVENTS.forEach(function (evt) {
      window.removeEventListener(evt, _trigger);
    });

    // Flush queue
    _queue.forEach(function (spec) {
      _runSpec(spec);
    });
    _queue = [];
  }

  function _runSpec(spec) {
    if (_registry[spec.id]) return;
    _registry[spec.id] = true;
    try {
      spec.load();
    } catch (e) {
      // Silently fail — a broken marketing script must never block commerce
      if (window.console && console.error) {
        console.error('[TsukieMarketing] Error loading "' + spec.id + '":', e);
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
     4. Event listeners + 5-second fallback
  ───────────────────────────────────────────────────────────────────────── */
  var _EVENTS = ['scroll', 'click', 'touchstart', 'keydown'];
  // Note: 'mousemove' intentionally omitted — it fires immediately on any mouse
  // movement, before meaningful user engagement. The 4 events above cover all
  // real interaction patterns while giving the LCP image a longer undisturbed window.

  _EVENTS.forEach(function (evt) {
    window.addEventListener(evt, _trigger, { once: true, passive: true });
  });

  // Fallback: fire 5 seconds after window.load regardless of interaction
  if (document.readyState === 'complete') {
    setTimeout(_trigger, 5000);
  } else {
    window.addEventListener('load', function () {
      setTimeout(_trigger, 5000);
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     5. Built-in script registrations
        GTM is registered first; GA4/Ads load inside GTM so they need no
        separate <script> tag — GTM handles them via its container.
        Facebook Pixel and Microsoft Clarity are registered independently.
  ───────────────────────────────────────────────────────────────────────── */

  /* ── 5a. Google Tag Manager ─────────────────────────────────────────── */
  var GTM_ID = window.TSUKIE_GTM_ID || 'GTM-XXXXXXX';  // Override via Liquid
  if (GTM_ID && GTM_ID !== 'GTM-XXXXXXX') {
    window.TsukieMarketing.register({
      id: 'gtm',
      load: function () {
        // Initialise dataLayer stub so pushes made before GTM loads are queued
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

        var s = document.createElement('script');
        s.async = true;
        s.src = 'https://www.googletagmanager.com/gtm.js?id=' + GTM_ID;
        document.head.appendChild(s);

        // GTM noscript iframe (best-effort, fires after body exists)
        var noscriptFn = function () {
          var ns = document.createElement('noscript');
          var iframe = document.createElement('iframe');
          iframe.src = 'https://www.googletagmanager.com/ns.html?id=' + GTM_ID;
          iframe.height = '0';
          iframe.width = '0';
          iframe.style.cssText = 'display:none;visibility:hidden';
          ns.appendChild(iframe);
          if (document.body) document.body.insertBefore(ns, document.body.firstChild);
        };
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', noscriptFn);
        } else {
          noscriptFn();
        }
      }
    });
  }

  /* ── 5b. Facebook Pixel ─────────────────────────────────────────────── */
  var FBPIXEL_ID = window.TSUKIE_FBPIXEL_ID || '';      // Override via Liquid
  if (FBPIXEL_ID) {
    window.TsukieMarketing.register({
      id: 'facebook-pixel',
      load: function () {
        // Skip if Shopify's native Facebook app embed already loaded fbq
        if (typeof window.fbq === 'function') return;

        (function (f, b, e, v, n, t, s) {
          if (f.fbq) return;
          n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = true;
          n.version = '2.0';
          n.queue = [];
          t = b.createElement(e);
          t.async = true;
          t.src = v;
          s = b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t, s);
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

        window.fbq('init', FBPIXEL_ID);
        window.fbq('track', 'PageView');

        // Noscript pixel image
        var ns = document.createElement('noscript');
        var img = document.createElement('img');
        img.height = 1;
        img.width = 1;
        img.style.cssText = 'display:none';
        img.src = 'https://www.facebook.com/tr?id=' + FBPIXEL_ID + '&ev=PageView&noscript=1';
        ns.appendChild(img);
        if (document.body) document.body.appendChild(ns);
      }
    });
  }

  /* ── 5c. Microsoft Clarity ──────────────────────────────────────────── */
  var CLARITY_ID = window.TSUKIE_CLARITY_ID || '';       // Override via Liquid
  if (CLARITY_ID) {
    window.TsukieMarketing.register({
      id: 'microsoft-clarity',
      load: function () {
        // Skip if already loaded (e.g. via Shopify App Embed)
        if (typeof window.clarity === 'function') return;

        (function (c, l, a, r, i, t, y) {
          c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
          t = l.createElement(r);
          t.async = 1;
          t.src = 'https://www.clarity.ms/tag/' + i;
          y = l.getElementsByTagName(r)[0];
          y.parentNode.insertBefore(t, y);
        })(window, document, 'clarity', 'script', CLARITY_ID);
      }
    });
  }

  /* ── 5d. Wigzo (if applicable — uncomment and set ID when needed) ───── */
  // var WIGZO_ID = window.TSUKIE_WIGZO_ID || '';
  // if (WIGZO_ID) {
  //   window.TsukieMarketing.register({
  //     id: 'wigzo',
  //     load: function () {
  //       var s = document.createElement('script');
  //       s.async = true;
  //       s.src = 'https://cdn.wigzo.com/pixel/v2/' + WIGZO_ID + '/js';
  //       document.head.appendChild(s);
  //     }
  //   });
  // }

})();
